import type { Session } from '#admin/features/auth/session.ts'

/**
 * Who may use the superadmin console. Its data layer is per domain beside this file (`tenants/`,
 * `plans/`, `users/`).
 *
 * **Platform staff only, and that is a different axis from every other permission here.** The
 * rest of this console asks `can(session, 'cms.write')`, which is a grant *inside* one tenant;
 * `platform:*` is the grant to act *across* tenants, and the two must not be conflated.
 */

/**
 * Is this session platform staff?
 *
 * **Deliberately not `can(session, 'platform:*')`.** `can` treats `*.*` as satisfying
 * everything, because inside a tenant it does — `*.*` is the tenant superadmin grant. It is not
 * platform staff, and the server draws that line hard: `requirePlatformScope` refuses `*.*` with
 * a 403, and there is an integration test asserting it ("a TENANT superadmin is refused").
 *
 * Reusing `can` here would show every tenant owner a Platform menu whose every page 403s —
 * which is worse than no menu, because it advertises a capability they do not have and looks
 * like a bug rather than a boundary.
 */
export function isPlatformStaff(session: Session | undefined): boolean {
  return session?.permissions.includes('platform:*') === true
}

/**
 * Does this member of staff hold a named power? `platform:users.manage`, `platform:impersonate`,
 * `platform:tenants.manage` — see the API's `platform-scopes.ts`.
 *
 * An exact match, for the reason `isPlatformStaff` gives: `can` would let `*.*` through, and
 * would split `platform:users.manage` on its dot. A button the server would refuse is worse than
 * no button.
 */
export function hasPlatformScope(session: Session | undefined, scope: PlatformScope): boolean {
  return isPlatformStaff(session) && session?.permissions.includes(scope) === true
}

export type PlatformScope =
  | 'platform:tenants.manage'
  | 'platform:plans.manage'
  | 'platform:payments.manage'
  | 'platform:users.manage'
  | 'platform:impersonate'
